<script setup lang="ts">
import { computed } from 'vue'

import { ICON_PATHS, isDirectionalIcon, type IconName } from './icons'

const props = withDefaults(
  defineProps<{
    name: IconName
    /** Pixel size; inherits the surrounding font size when omitted. */
    size?: number
    /**
     * Accessible name. Omit for icons that only decorate a labelled control —
     * those are hidden from assistive technology, which is the correct default.
     */
    label?: string
    strokeWidth?: number
    /**
     * Whether the glyph mirrors under `dir="rtl"`. Defaults to the icon's own
     * nature (see `DIRECTIONAL_ICONS`): a chevron flips, a checkmark does not.
     * Set it explicitly for the rare case where the surrounding layout, not the
     * reading direction, decides — a chevron that always points down-page, say.
     */
    flipRtl?: boolean | undefined
  }>(),
  // `flipRtl: undefined` is load-bearing: Vue casts an absent Boolean prop to
  // `false` unless a default is declared, which would defeat the `??` below and
  // stop every chevron from mirroring.
  { size: 16, label: '', strokeWidth: 1.75, flipRtl: undefined },
)

const path = computed(() => ICON_PATHS[props.name])
const decorative = computed(() => props.label === '')
const mirrors = computed(() => props.flipRtl ?? isDirectionalIcon(props.name))
</script>

<template>
  <svg
    class="e-icon"
    :class="{ 'e-icon--directional': mirrors }"
    :width="size"
    :height="size"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    :stroke-width="strokeWidth"
    stroke-linecap="round"
    stroke-linejoin="round"
    :aria-hidden="decorative ? 'true' : undefined"
    :role="decorative ? undefined : 'img'"
    :aria-label="decorative ? undefined : label"
    focusable="false"
  >
    <title v-if="!decorative">{{ label }}</title>
    <path :d="path" />
  </svg>
</template>

<style>
.e-icon {
  display: inline-block;
  flex: none;
  vertical-align: middle;
}

/*
 * Only glyphs that point along the reading axis mirror. Everything else keeps
 * its drawing, which is why this is a class the component opts into rather than
 * a rule on `.e-icon`.
 */
[dir='rtl'] .e-icon--directional {
  transform: scaleX(-1);
}
</style>
